<template>
  <div v-if="isValid" class="trbo-a-range">
    <div class="trbo-a-range__inputs">
      <div class="trbo-a-range__inputs__item">
        <input
          v-model="inputLower"
          class="trbo-a-range__inputs__item__input"
          type="number"
          aria-label="Minimum"
          :style="inputStyles"
          @blur="formatLower"
          @keydown.enter.prevent="handleLowerSubmit"
        >
        <span ref="lowerUnitSymbol" class="trbo-a-range__inputs__item__metric-symbol">
          {{ unitSymbol }}
        </span>
      </div>
      <div class="trbo-a-range__inputs__item">
        <input
          v-model="inputHigher"
          class="trbo-a-range__inputs__item__input"
          type="number"
          aria-label="Maximum"
          :style="inputStyles"
          @blur="formatHigher"
          @keydown.enter.prevent="handleHigherSubmit"
        >
        <span class="trbo-a-range__inputs__item__metric-symbol">
          {{ unitSymbol }}
        </span>
      </div>
    </div>
    <div class="trbo-a-range__range">
      <vue-slider
        v-model="range"
        tooltip="none"
        :dot-size="16"
        :min="minValue"
        :max="maxValue"
        :interval="stepSize"
        :min-range="stepSize"
        @change="onSliderChange"
      />
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component'

import { formatStepSize, isDivisible } from '../../../../../common/utils'

export default {
  components: {
    VueSlider
  },
  props: {
    question: {
      type: Object, // WizardPage
      required: true
    },
    questionData: {
      type: Object, // WizardPageData<RangeData>
      required: true
    },
    selected: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      range: [],
      inputLower: 0,
      inputHigher: 0,
      forceNextPage: false,
      unitSymbolWidth: 16
    }
  },
  computed: {
    isValid () {
      if (!this.questionData || this.questionData.minValue === undefined || this.questionData.maxValue === undefined) return false

      return isDivisible(this.maxValue - this.minValue, this.stepSize)
    },
    minValue () {
      return Number(this.questionData.minValue)
    },
    maxValue () {
      return Number(this.questionData.maxValue)
    },
    stepSize () {
      return Number(this.questionData.stepSize)
    },
    unitSymbol () {
      return this.questionData.unitSymbol
    },
    inputStyles () {
      return {
        '--input-right-spacing': `${this.unitSymbolWidth}px`
      }
    }
  },
  watch: {
    questionData () {
      this.$nextTick(() => this.setRangeData())
    }
  },
  mounted () {
    this.setRangeData()
    this.setUnitSymbolWidth()
  },
  methods: {
    setRangeData () {
      if (this.selected.length > 0) {
        this.range = this.selected.map(value => Number(value))
        this.inputLower = this.range[0]
        this.inputHigher = this.range[1]
      } else if (this.questionData) {
        const minValue = Number(this.questionData.minValue)
        const maxValue = Number(this.questionData.maxValue)
        this.range = [minValue, maxValue]
        if (this.question.optional) {
          this.inputLower = minValue
          this.inputHigher = maxValue
        } else {
          this.onSliderChange([minValue, maxValue])
        }
      }
    },
    onSliderChange (value) {
      this.$nextTick(() => {
        this.inputLower = value[0]
        this.inputHigher = value[1]
        this.onChange()
      })
    },
    formatLower (event) {
      let newValue = Number(event.target.value)

      if (newValue < this.minValue) newValue = this.minValue
      if (newValue >= this.inputHigher) newValue = this.inputHigher - this.stepSize

      newValue = formatStepSize(newValue, this.stepSize)
      this.inputLower = newValue
      this.range = [newValue, this.inputHigher]

      this.$nextTick(() => this.onChange())
    },
    formatHigher (event) {
      let newValue = Number(event.target.value)

      if (newValue <= this.inputLower || (newValue - this.stepSize) <= this.inputLower) newValue = this.inputLower + this.stepSize
      if (newValue > this.maxValue) newValue = this.maxValue

      newValue = formatStepSize(newValue, this.stepSize)
      this.inputHigher = newValue
      this.range = [this.inputLower, newValue]

      this.$nextTick(() => this.onChange())
    },
    onChange () {
      const value = [...this.range]
      this.$emit('submit', { value, forceNextPage: this.forceNextPage })
    },
    handleLowerSubmit (event) {
      this.forceNextPage = true
      this.formatLower(event)
    },
    handleHigherSubmit (event) {
      this.forceNextPage = true
      this.formatHigher(event)
    },
    setUnitSymbolWidth () {
      if (!this.$refs.lowerUnitSymbol) return

      const rect = this.$refs.lowerUnitSymbol.getBoundingClientRect()
      this.unitSymbolWidth = rect.width
    }
  }
}
</script>

<style src="./custom-vue-slider-component.scss" />
<style scoped src="./range.scss" />
